import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import MarkdownContent from "@/components/common/MarkdownContent";
import {
  Film,
  CheckCircle2,
  MessageSquarePlus,
  X,
  Clock,
  Sparkles,
  AlertTriangle,
} from "lucide-react";
import { getCanonicalPlanDisplay } from "@/domain/productionPlan";

export const ProductionPlanModal = ({
  open,
  onClose,
  plan,
  project,
  isActive = false,
  onActivatePlan,
  onRequestChanges,
}) => {
  const [showChangesInput, setShowChangesInput] = useState(false);
  const [changesInstructions, setChangesInstructions] = useState("");
  const [isActivating, setIsActivating] = useState(false);

  if (!plan) return null;

  const { displayTitle, canonicalVersion } = getCanonicalPlanDisplay(plan);
  const projectName = project?.name || "Miss Meddle";
  const projectId = project?.id || plan.projectId || "";

  const handleActivate = () => {
    setIsActivating(true);
    if (onActivatePlan) {
      onActivatePlan(plan);
    }
    setTimeout(() => {
      setIsActivating(false);
      onClose();
    }, 150);
  };

  const handleConfirmChanges = () => {
    if (!changesInstructions.trim()) return;
    if (onRequestChanges) {
      onRequestChanges(plan, changesInstructions.trim());
    }
    // No reseteamos changesInstructions para preservarlas si el usuario reabre
    setShowChangesInput(false);
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(val) => !val && onClose()}>
      <DialogContent
        data-testid="production-plan-modal"
        className="max-w-4xl w-[95vw] sm:w-[90vw] md:max-w-4xl h-[90vh] max-h-[90vh] flex flex-col p-0 gap-0 overflow-hidden bg-slate-900 border-slate-700 text-slate-100 shadow-2xl"
      >
        {/* Cabecera Fija */}
        <DialogHeader className="px-6 py-4 border-b border-slate-800 bg-slate-900/95 flex-shrink-0">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-xl bg-indigo-500/10 border border-indigo-500/20 text-indigo-400">
                <Film className="w-5 h-5" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <DialogTitle className="text-lg font-semibold text-slate-100">
                    {displayTitle}
                  </DialogTitle>
                  <span className="px-2 py-0.5 rounded text-xs font-semibold bg-indigo-500/20 text-indigo-300 border border-indigo-500/30">
                    {canonicalVersion}
                  </span>
                  {isActive && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-semibold bg-emerald-500/20 text-emerald-300 border border-emerald-500/30">
                      <CheckCircle2 className="w-3 h-3" />
                      Plan Activo
                    </span>
                  )}
                </div>
                <DialogDescription className="text-xs text-slate-400 mt-0.5">
                  Proyecto: <span className="text-slate-200 font-medium">{projectName}</span> ({projectId})
                </DialogDescription>
              </div>
            </div>
          </div>
        </DialogHeader>

        {/* Cuerpo con texto formateado y UN ÚNICO scroll vertical */}
        <div className="flex-1 p-6 overflow-y-auto bg-slate-950/60 leading-relaxed space-y-4">
          {plan.hasDurationDiscrepancy && (
            <div
              data-testid="plan-duration-discrepancy-modal"
              className="p-3.5 rounded-xl bg-amber-950/40 border border-amber-500/40 text-amber-200 text-xs space-y-1.5"
            >
              <div className="flex items-center gap-2 font-semibold text-amber-300">
                <AlertTriangle className="w-4 h-4 text-amber-400 flex-shrink-0" />
                Discrepancia detectada con las duraciones del Storyboard
              </div>
              <p className="text-amber-300/80 leading-relaxed text-[11px]">
                El plan propone duraciones que difieren de las tomas preparadas en el Storyboard:
              </p>
              <ul className="list-disc list-inside text-amber-200/90 pl-1 space-y-0.5 text-[11px]">
                {plan.durationDiscrepancies.map((d) => (
                  <li key={d.order}>
                    Toma {d.order}: el plan propone {d.planDuration}s (el Storyboard tiene {d.storyboardDuration}s).
                  </li>
                ))}
              </ul>
              <p className="text-[11px] text-amber-400/80 pt-0.5">
                Las duraciones del Storyboard no se han modificado automáticamente. Puedes solicitar una corrección o conservar las duraciones de tu Storyboard.
              </p>
            </div>
          )}

          {plan.hasContractError && (
            <div
              data-testid="plan-contract-error-modal"
              className="p-3.5 rounded-xl bg-rose-950/40 border border-rose-500/40 text-rose-200 text-xs space-y-1.5"
            >
              <div className="flex items-center gap-2 font-semibold text-rose-300">
                <AlertTriangle className="w-4 h-4 text-rose-400 flex-shrink-0" />
                Estructura no conforme con el contrato VEYRA
              </div>
              <p className="text-rose-300/80 leading-relaxed text-[11px]">
                El bloque emitido carece de campos obligatorios para su activación segura:
              </p>
              <ul className="list-disc list-inside text-rose-200/90 pl-1 space-y-0.5 text-[11px]">
                {plan.contractErrors.map((err, i) => (
                  <li key={i}>{err}</li>
                ))}
              </ul>
              <p className="text-[11px] text-rose-400/80 pt-0.5">
                El contenido completo se conserva íntegro a continuación para su lectura y revisión.
              </p>
            </div>
          )}

          {plan.summary && (
            <div className="p-3.5 rounded-lg bg-indigo-950/30 border border-indigo-500/20 text-indigo-200/90 text-xs">
              <span className="font-semibold text-indigo-300 mr-1.5">Resumen consultivo:</span>
              {plan.summary}
            </div>
          )}

          {/* Formulario desplegable para solicitar cambios */}
          {showChangesInput && (
            <div className="p-4 rounded-xl bg-slate-800/90 border border-indigo-500/30 shadow-lg space-y-3 animate-in fade-in duration-200">
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold text-indigo-300 flex items-center gap-1.5">
                  <MessageSquarePlus className="w-4 h-4" />
                  Instrucciones de cambio para el Creative Director
                </span>
                <button
                  type="button"
                  onClick={() => setShowChangesInput(false)}
                  className="text-slate-400 hover:text-slate-200 p-1"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
              <p className="text-xs text-slate-400">
                Escribe ajustes sencillos (ej. «prioriza menor coste», «reduce generaciones de vídeo», «ajusta la persiana»). Se preparará una solicitud en el chat sin enviarla automáticamente.
              </p>
              <textarea
                value={changesInstructions}
                onChange={(e) => setChangesInstructions(e.target.value)}
                placeholder="Escribe tus instrucciones de ajuste aquí..."
                rows={3}
                className="w-full p-2.5 text-xs rounded-lg bg-slate-900 border border-slate-700 text-slate-100 placeholder-slate-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 resize-none"
              />
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setShowChangesInput(false)}
                  className="px-3 py-1.5 text-xs text-slate-400 hover:text-slate-200 bg-slate-800 rounded-md"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={handleConfirmChanges}
                  disabled={!changesInstructions.trim()}
                  className="px-3 py-1.5 text-xs font-medium text-white bg-indigo-600 hover:bg-indigo-500 disabled:opacity-50 rounded-md transition-colors"
                >
                  Preparar solicitud en chat
                </button>
              </div>
            </div>
          )}

          <div className="prose prose-invert prose-sm max-w-none prose-headings:text-slate-200 prose-p:text-slate-300 prose-li:text-slate-300">
            <MarkdownContent content={plan.content || "Sin contenido disponible para este plan."} />
          </div>
        </div>

        {/* Pie Fijo */}
        <DialogFooter className="px-6 py-4 border-t border-slate-800 bg-slate-900/95 flex-shrink-0 flex items-center justify-between sm:justify-between gap-3">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-xs font-medium text-slate-300 hover:text-white bg-slate-800 hover:bg-slate-700 border border-slate-700 rounded-lg transition-colors"
          >
            Cerrar
          </button>

          <div className="flex items-center gap-2.5">
            <button
              type="button"
              onClick={() => setShowChangesInput((prev) => !prev)}
              className="inline-flex items-center gap-1.5 px-3.5 py-2 text-xs font-medium text-indigo-300 hover:text-indigo-200 bg-indigo-950/40 hover:bg-indigo-900/50 border border-indigo-700/50 rounded-lg transition-colors"
            >
              <MessageSquarePlus className="w-3.5 h-3.5" />
              Solicitar cambios
            </button>

            <button
              type="button"
              onClick={handleActivate}
              disabled={isActive || isActivating || plan.hasContractError}
              className={`inline-flex items-center gap-1.5 px-4 py-2 text-xs font-medium rounded-lg shadow-sm transition-colors ${
                isActive
                  ? "bg-slate-800 text-slate-400 border border-slate-700 cursor-default"
                  : plan.hasContractError
                  ? "bg-slate-800/80 text-slate-500 border border-slate-700/60 cursor-not-allowed"
                  : "bg-indigo-600 hover:bg-indigo-500 text-white"
              }`}
            >
              <CheckCircle2 className="w-3.5 h-3.5" />
              {isActive
                ? "Planificación activa"
                : plan.hasContractError
                ? "Plan no activable (estructura incompleta)"
                : "Usar esta planificación"}
            </button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ProductionPlanModal;
